import React, { useMemo } from 'react'
import { FieldArray, FormikProps } from 'formik'
import { ButtonV2, TextInputV4 } from '@labrav/react-components'
import { get } from 'lodash'
import {
  FieldMetadata,
  FieldMetadataMetaData,
  MetaData,
} from '../../../types/flyout'
import { useStyles } from './CustomMetaDataV2.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { getRandom } from '../../utils/helperFunctions'
import clsx from 'clsx'
import { InputSize } from '@labrav/react-components/lib/@types/v2/components/DynamicInput/DynamicInput'
import { useSelector } from '../../../store'

interface CustomMetaDataV2Props {
  formik: FormikProps<Record<string, unknown>>
  subHeading?: React.ReactNode
  addButtonName: string
  size?: InputSize
  product?: { productId: string }
}

export const CustomMetaDataV2 = ({
  formik,
  subHeading,
  addButtonName,
  size = 'medium',
  product,
}: CustomMetaDataV2Props) => {
  const { classes } = useStyles()
  const { values, errors, touched, handleChange, handleBlur } = formik
  const metaData = (values.metaData || []) as MetaData[]
  const emptyMetaData = { key: '', value: '' }
  const randomKey = useMemo(() => getRandom(), [metaData.length])

  const fieldMetadata = useSelector<FieldMetadata | undefined>(state => {
    const root = state as {
      privateOffersInCreation?: {
        currentPrivateOfferInCreation?: Record<
          string,
          { fieldMetadata?: FieldMetadata }
        >
      }
    }
    return product?.productId
      ? root.privateOffersInCreation?.currentPrivateOfferInCreation?.[
          product.productId
        ]?.fieldMetadata
      : undefined
  })

  const fieldMetadataMetaData =
    fieldMetadata?.metadata as FieldMetadataMetaData[]

  return (
    <>
      {subHeading}
      <div
        className={clsx(classes.root, classes.elementsGap)}
        data-testid="update-metadata"
      >
        <FieldArray name="metaData">
          {({ push, remove }) => (
            <>
              {metaData.map((currMetaData, index) => {
                const isRowReadOnly =
                  fieldMetadataMetaData?.find(
                    meta => meta.key === currMetaData.key
                  )?.readOnly ?? false

                return (
                  <div
                    key={`metaData-row-${randomKey}-${index}`}
                    className={classes.resourceRow}
                    data-testid="metaData-row"
                  >
                    <div className={classes.resourcesContainer}>
                      <div
                        className={classes.resouceField}
                        key={`metaData-${randomKey}-key-${index}`}
                      >
                        <TextInputV4
                          keyName={`metaData.${[index]}.key`}
                          title="Key"
                          type="text"
                          placeHolder=""
                          value={currMetaData?.key as string}
                          isError={
                            !!(
                              get(touched, `metaData.${[index]}.key`, false) &&
                              get(errors, `metaData.${[index]}.key`, false)
                            )
                          }
                          errorText={
                            (!!get(touched, `metaData.${[index]}.key`, false) &&
                              get(errors, `metaData.${[index]}.key`, '')) ||
                            ''
                          }
                          onChange={handleChange}
                          isReadOnly={isRowReadOnly ?? false}
                          isRequired={false}
                          showSpinner={false}
                          onBlur={handleBlur}
                        />
                      </div>
                      <div
                        className={classes.resouceField}
                        key={`metaData-${randomKey}-value-${index}`}
                      >
                        <TextInputV4
                          keyName={`metaData.${[index]}.value`}
                          title="Value"
                          type="text"
                          placeHolder=""
                          value={currMetaData?.value as string}
                          isError={
                            !!(
                              get(
                                touched,
                                `metaData.${[index]}.value`,
                                false
                              ) &&
                              get(errors, `metaData.${[index]}.value`, false)
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `metaData.${[index]}.value`,
                              false
                            ) &&
                              get(errors, `metaData.${[index]}.value`, '')) ||
                            ''
                          }
                          onChange={handleChange}
                          isReadOnly={isRowReadOnly ?? false}
                          isRequired={false}
                          showSpinner={false}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                    <div
                      className={clsx(
                        classes.trashIcon,
                        size === 'small' && classes.smallTrashIcon,
                        isRowReadOnly && classes.readOnlyRow
                      )}
                    >
                      <FontAwesomeIcon
                        icon={faTrash}
                        style={{ width: '18px', height: '20px' }}
                        data-testid="trash-metaData"
                        onClick={() => remove(index)}
                      />
                    </div>
                  </div>
                )
              })}

              <ButtonV2
                className={classes.widthButton}
                styleType="link"
                onClick={() => {
                  push(emptyMetaData)
                }}
                data-testid="add-a-metaData-button"
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  width={'14px'}
                  height={'16px'}
                  style={{ paddingRight: '13px', paddingLeft: '5px' }}
                />
                {addButtonName}
              </ButtonV2>
            </>
          )}
        </FieldArray>
      </div>
    </>
  )
}
